import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ui from "../data/locales/ui";
import { localizeContent } from "./content";

export const languages = [
  { code: "ko", label: "KO", name: "한국어", flag: "🇰🇷" },
  { code: "en", label: "EN", name: "English", flag: "🇺🇸" },
  { code: "ja", label: "JA", name: "日本語", flag: "🇯🇵" },
];
export const getLocale = (search) => {
  const value = new URLSearchParams(search).get("lang");
  return languages.some((language) => language.code === value) ? value : "ko";
};
export function translate(locale, key, values = {}) {
  return (ui[locale]?.[key] || ui.ko[key] || key).replace(
    /\{(\w+)\}/g,
    (match, name) => values[name] ?? match,
  );
}
const I18nContext = createContext({
  locale: "ko",
  t: (key, values) => translate("ko", key, values),
});

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = getLocale(location.search);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const value = useMemo(
    () => ({
      locale,
      t: (key, values) => translate(locale, key, values),
      setLocale: (next) => {
        if (
          !languages.some((language) => language.code === next) ||
          next === locale
        )
          return;
        const params = new URLSearchParams(location.search);
        params.set("lang", next);
        navigate({
          pathname: location.pathname,
          search: `?${params}`,
          hash: location.hash,
        });
      },
      // Retain the selected language in normal navigation and copy/open-in-new-tab links.
      localizedPath: (pathname) => ({
        pathname,
        search:
          locale === "ko" && !new URLSearchParams(location.search).has("lang")
            ? ""
            : `?lang=${locale}`,
      }),
    }),
    [locale, location.pathname, location.search, location.hash, navigate],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
export const useI18n = () => useContext(I18nContext);
export function usePortfolioData() {
  const { locale } = useI18n();
  return useMemo(() => localizeContent(locale), [locale]);
}
